import { Link, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { ArrowLeft, ArrowRight, Award } from 'lucide-react';
import { skillPathBySlug } from '../data/learningContent';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { usePlan } from '../lib/usePlan';
import UpgradeModal from '../components/UpgradeModal';
import LessonCard from '../components/LessonCard';
import { toast } from 'sonner';
import { useDocumentMeta } from '../lib/useDocumentMeta';

const PATH_SKILL: Record<string, string> = {
  beginners: 'asking better questions',
  learners: 'thinking with AI',
  parents: 'guiding curiosity',
  daily: 'using AI in daily life',
  creative: 'creating with AI',
  work: 'AI for work',
  jobs: 'AI for the job search',
};

export default function SkillPathDetailPage() {
  const { slug } = useParams();
  const { user, profile } = useAuth();
  const plan = usePlan();
  const path = slug ? skillPathBySlug(slug) : null;
  useDocumentMeta({
    title: path ? `${path.title} — PromptGPT` : 'Path — PromptGPT',
    description: path?.description || 'Practical AI skill path on PromptGPT.',
    canonical: `https://promptgpt.ai/skill-paths/${slug || ''}`,
  });
  const [completed, setCompleted] = useState<Set<string>>(new Set());
  const [upgrade, setUpgrade] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const [certificate, setCertificate] = useState<{ verification_code: string } | null>(null);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data } = await supabase
        .from('user_progress')
        .select('lesson_slug, status')
        .eq('user_id', user.id)
        .eq('status', 'completed');
      setCompleted(new Set((data || []).map((r: { lesson_slug: string }) => r.lesson_slug)));
    })();
  }, [user]);

  useEffect(() => {
    if (!user || !slug) return;
    (async () => {
      const { data } = await supabase
        .from('certificates')
        .select('verification_code')
        .eq('user_id', user.id)
        .eq('skill_path_slug', slug)
        .maybeSingle();
      if (data) setCertificate(data);
    })();
  }, [user, slug]);

  if (!path) {
    return (
      <div className="container-app py-12 max-w-xl">
        <p>Path not found.</p>
        <Link to="/skill-paths" className="btn-secondary mt-4">Back to paths</Link>
      </div>
    );
  }

  const lessons = path.modules.flatMap((m) => m.lessons);
  const doneCount = lessons.filter((l) => completed.has(l.slug)).length;
  const nextLesson = path.modules
    .flatMap((m) => m.lessons.map((l) => ({ m, l })))
    .find(({ l }) => !completed.has(l.slug));
  const isLimited = !plan.isPro;
  const proPathLocked = !!path.proOnly && !plan.isPro;
  const moduleLocked = (idx: number) => proPathLocked || (isLimited && idx > 0);
  const skill = PATH_SKILL[path.slug] ?? 'AI fluency';

  const issueCertificate = async () => {
    if (!user) return;
    if (!plan.hasCertificates) {
      setUpgrade(true);
      return;
    }
    setIssuing(true);
    const code = `${path.slug.slice(0, 3).toUpperCase()}-${Math.random().toString(36).slice(2, 8).toUpperCase()}-${Date.now().toString(36).toUpperCase()}`;
    const { data, error } = await supabase
      .from('certificates')
      .insert({
        user_id: user.id,
        skill_path_slug: path.slug,
        skill_path_title: path.title,
        recipient_name: profile?.full_name || 'PromptGPT learner',
        verification_code: code,
      })
      .select('verification_code')
      .maybeSingle();
    setIssuing(false);
    if (error || !data) {
      toast.error('Could not issue certificate');
      return;
    }
    setCertificate(data);
    toast.success('Certificate issued');
  };

  return (
    <div className="bg-warm-50 min-h-screen">
      <div className="container-app py-10 max-w-3xl">
        <Link to="/skill-paths" className="inline-flex items-center gap-2 text-sm text-ink-500 hover:text-ink-900 mb-8">
          <ArrowLeft className="w-4 h-4" /> All paths
        </Link>

        <div className="flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-500 mb-3">
          <span>Path · {skill}</span>
          {path.proOnly && (
            <span className="bg-ink-900 text-warm-50 px-1.5 py-0.5 rounded-full tracking-wider">Pro</span>
          )}
        </div>
        <h1 className="font-display text-4xl sm:text-5xl mb-4 leading-tight">{path.title}</h1>
        <p className="text-ink-600 text-lg leading-relaxed mb-6">{path.description}</p>

        {proPathLocked && (
          <div className="card p-5 mb-8 bg-white border border-ink-200 flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="font-medium mb-1">This is a Pro library path.</div>
              <p className="text-sm text-ink-600">Upgrade to Pro to unlock {path.title} along with every other path and a new drop every month.</p>
            </div>
            <button onClick={() => setUpgrade(true)} className="btn-primary text-sm">Upgrade to Pro <ArrowRight className="w-4 h-4" /></button>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-4 mb-10 text-sm text-ink-600">
          <span>{doneCount} of {lessons.length} challenges</span>
          <span>·</span>
          <span>{Math.round(path.totalMinutes / 60)} hours total</span>
          {!proPathLocked && nextLesson ? (
            <Link
              to={`/skill-paths/${path.slug}/${nextLesson.l.slug}`}
              className="btn-primary ml-auto"
            >
              {doneCount === 0 ? 'Start path' : 'Continue'} <ArrowRight className="w-4 h-4" />
            </Link>
          ) : !proPathLocked && doneCount === lessons.length ? (
            certificate ? (
              <Link to={`/certificates/${certificate.verification_code}`} className="btn-secondary ml-auto">
                <Award className="w-4 h-4" /> View certificate
              </Link>
            ) : (
              <button onClick={issueCertificate} disabled={issuing} className="btn-secondary ml-auto disabled:opacity-50">
                <Award className="w-4 h-4" /> {issuing ? 'Issuing…' : 'Issue certificate'}
              </button>
            )
          ) : null}
        </div>

        <div className="space-y-10">
          {path.modules.map((m, idx) => {
            const locked = moduleLocked(idx);
            return (
              <div key={m.slug}>
                <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-500 mb-2">
                  Series {idx + 1}{locked && ' · Pro'}
                </div>
                <h2 className="font-display text-2xl mb-1">{m.title}</h2>
                <p className="text-ink-600 mb-5">{m.description}</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {m.lessons.map((l) => {
                    const isDone = completed.has(l.slug);
                    const status: 'done' | 'current' | 'upcoming' | 'locked' = locked
                      ? 'locked'
                      : isDone
                      ? 'done'
                      : nextLesson?.l.slug === l.slug
                      ? 'current'
                      : 'upcoming';
                    return (
                      <LessonCard
                        key={l.slug}
                        to={locked ? '#' : `/skill-paths/${path.slug}/${l.slug}`}
                        title={l.title}
                        objective={l.summary}
                        duration={l.duration}
                        skill={skill}
                        status={status}
                      />
                    );
                  })}
                </div>
                {locked && (
                  <button
                    onClick={() => setUpgrade(true)}
                    className="text-sm text-ink-700 hover:text-ink-900 mt-4 inline-flex items-center gap-1"
                  >
                    Unlock with Pro <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <UpgradeModal
        open={upgrade}
        onClose={() => setUpgrade(false)}
        feature="Full skill paths"
        reason="Free includes the first series of every path. Upgrade to Pro for the full path and certificates."
      />
    </div>
  );
}
